import React from 'react'
import { motion } from 'framer-motion'
import { Star, Quote } from 'lucide-react'
import { CONFIG } from '../config/constants'

interface GoogleReview {
  author_name: string
  profile_photo_url?: string
  rating: number
  relative_time_description: string
  text: string
}

const GoogleReviews: React.FC = () => {
  // 🔹 Actual Google Reviews for Body Science Rehab Clinic - Dr. Priyanshu Chouhan
  // These are real patient reviews from Google
  // To fetch live reviews automatically in the future, you can:
  // 1. Set up Google Places API
  // 2. Get your Place ID from: https://developers.google.com/maps/documentation/places/web-service/place-id
  // 3. Use the Places API to fetch reviews dynamically
  
  const reviews: GoogleReview[] = [
    {
      author_name: 'Akansha Sharma',
      profile_photo_url: 'https://ui-avatars.com/api/?name=Akansha+Sharma&background=0ea5e9&color=fff',
      rating: 5,
      relative_time_description: '6 months ago',
      text: 'I had an amazing experience at Body Science Rehab! The physiotherapist took the time to understand my pain and provided a personalized treatment plan that worked wonders. After just a few sessions, I noticed a significant improvement in my mobility and pain relief. If you\'re looking for expert care in Jodhpur, I highly recommend Body Science Rehab.',
    },
    {
      author_name: 'Chinmay Bohra',
      profile_photo_url: 'https://ui-avatars.com/api/?name=Chinmay+Bohra&background=10b981&color=fff',
      rating: 5,
      relative_time_description: '1 month ago',
      text: 'I visited Body Science Rehab clinic and met Dr Priyanshu Sir. I suffered from ligament injury of ankle and taken the rehab for the same. After my treatment was completely done, I have attained full recovery. When I came to clinic I was not able to walk properly, but after the treatment I am completely fine now.',
    },
    {
      author_name: 'Veena Sakhrani',
      profile_photo_url: 'https://ui-avatars.com/api/?name=Veena+Sakhrani&background=f59e0b&color=fff',
      rating: 5,
      relative_time_description: '2 months ago',
      text: 'I am really very thankful to you Dr. Priyanshu. When I was suffering with my shoulder issues, my orthopedic doctor told me that I am having rotator cuff tear and I suffered for 4 months. When I met with you and taken your treatment, I got relief and now I am much better. Thank you so much for your support.',
    },
    {
      author_name: 'Mayank Golani',
      profile_photo_url: 'https://ui-avatars.com/api/?name=Mayank+Golani&background=8b5cf6&color=fff',
      rating: 5,
      relative_time_description: '3 months ago',
      text: 'I visited Dr. Priyanshu Chouhan at Body Science Rehab in Jodhpur for a consultation regarding muscle spasm. He was very polite, listened carefully, and explained everything in simple words. I felt comfortable and well-guided. Highly recommended for anyone needing advice on such issues.',
    },
    {
      author_name: 'Khanak Singh',
      profile_photo_url: 'https://ui-avatars.com/api/?name=Khanak+Singh&background=ef4444&color=fff',
      rating: 5,
      relative_time_description: '3 months ago',
      text: 'My grandfather was having low back pain since 15 days, so we decided to visit Body Science Rehab for a consultation. He had taken treatment from BSR clinic. He was having difficulty in sitting, walking and from sitting to standing. After the treatment he is much better now and able to do his daily activities with ease.',
    },
    {
      author_name: 'Rajvirendra Singh',
      profile_photo_url: 'https://ui-avatars.com/api/?name=Rajvirendra+Singh&background=ec4899&color=fff',
      rating: 5,
      relative_time_description: '5 months ago',
      text: 'I was suffering from neck pain since 15 days so I decided to consult a doctor regarding my problem. Someone suggested me about Body Science Rehab Clinic, so I decided to take a consultation from Dr Priyanshu sir. The advices and treatment were excellent and I got relief from my pain.',
    },
  ]

  // ⭐ Utility to render star rating
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-5 h-5 ${
          index < rating
            ? 'text-yellow-400 fill-yellow-400'
            : 'text-gray-300 dark:text-gray-600'
        }`}
      />
    ))
  }

  return (
    <section className="relative z-10 bg-white dark:bg-gray-900 section-padding">
      <div className="container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full mb-6"
          >
            <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
          </motion.div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 px-4">
            What Our Patients Say
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto px-4">
            Real experiences from our valued patients at {CONFIG.businessName}
          </p>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto">
          {reviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}
              className="card p-5 sm:p-6 relative"
            >
              {/* Quote Icon */}
              <div className="absolute top-4 right-4 opacity-10">
                <Quote className="w-12 h-12 text-primary-600" />
              </div>

              {/* Author Info */}
              <div className="flex items-center gap-4 mb-4">
                <motion.img
                  whileHover={{ scale: 1.1 }}
                  src={review.profile_photo_url}
                  alt={review.author_name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-primary-100 dark:ring-primary-900"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {review.author_name}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {review.relative_time_description}
                  </p>
                </div>
              </div>

              {/* Star Rating */}
              <div className="flex items-center gap-1 mb-4">
                {renderStars(review.rating)}
              </div>

              {/* Review Text */}
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
                "{review.text}"
              </p>

              {/* Google Badge */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Posted on Google</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Have you experienced our services? Share your feedback!
          </p>
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="https://www.google.com/search?sca_esv=fe33c2d79fc146ed&rlz=1C5CHFA_enIN1149IN1149&sxsrf=AE3TifP03mmNrCES6kaNxdF3I8BPW3vfkA:1761648887514&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E6gXUQWzL17paOPtGkEAMkC8hXh_uluDV6lwa-ZE6qJJoNf8xm7ThOp5MKOgxyLTil5AGkBMXDq9QpbQcQsJLpASRzXghB9VXOEn5IabG-YdvTjXhe46iGXjbWQqp50LQ35p6m6cXSdnaqJ7tbXta5NtRBje&q=Body+Science+Rehab+Clinic+-+Dr.+Priyanshu+Chouhan+Reviews&sa=X&ved=2ahUKEwi48KLB3caQAxX0TWwGHdV4IKwQ0bkNegQINhAD&biw=1792&bih=891&dpr=2"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 btn btn-primary"
          >
            <Star className="w-5 h-5" />
            Write a Review on Google
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}

export default GoogleReviews
